import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Briefcase,
  Building,
  Shield,
  History,
  FileText,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Conflicts = () => {
  // Search state
  const [searchName, setSearchName] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Clear conflict modal state
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearingCheck, setClearingCheck] = useState(null);
  const [clearNotes, setClearNotes] = useState('');
  const [submittingClear, setSubmittingClear] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/conflicts/history?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(response.data.checks || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchName.trim() && !searchCompany.trim()) {
      setError('Please enter a name or company to search');
      return;
    }

    setSearching(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/conflicts/check`,
        {
          name: searchName.trim() || null,
          company: searchCompany.trim() || null,
          check_type: 'new_client'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
      fetchHistory(); // Refresh history
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run conflict check');
    } finally {
      setSearching(false);
    }
  };

  const openClearModal = (check) => {
    setClearingCheck(check);
    setClearNotes('');
    setShowClearModal(true);
  };

  const handleClearConflict = async () => {
    if (!clearingCheck) return;

    setSubmittingClear(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/conflicts/${clearingCheck.check_id}/clear`,
        { notes: clearNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowClearModal(false);
      setClearingCheck(null);
      fetchHistory();
    } catch (err) {
      alert('Failed to clear conflict: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmittingClear(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'clear': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'conflict': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'potential': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'cleared': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'clear': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'conflict': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'potential': return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'cleared': return <Shield className="w-5 h-5 text-blue-400" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getMatchTypeIcon = (matchType) => {
    switch (matchType) {
      case 'contact': return <Users className="w-4 h-4" />;
      case 'case': return <Briefcase className="w-4 h-4" />;
      case 'adverse_party': return <AlertTriangle className="w-4 h-4" />;
      case 'case_contact': return <FileText className="w-4 h-4" />;
      default: return <Building className="w-4 h-4" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-gradient-to-br from-amber-500/20 to-red-500/20 rounded-xl border border-amber-500/30">
          <Shield className="w-8 h-8 text-amber-400" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Conflict Check</h1>
          <p className="text-virgo-teal-300 mt-1">Search for potential conflicts of interest before accepting new clients</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Search Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search Form */}
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-virgo-teal-400" />
              New Conflict Search
            </h2>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Person Name
                  </label>
                  <input
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="e.g., John Smith"
                    className="w-full px-4 py-2.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    value={searchCompany}
                    onChange={(e) => setSearchCompany(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className="w-full px-4 py-2.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>
              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={searching}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              >
                {searching ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Run Conflict Check
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Results */}
          {results && (
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  {getStatusIcon(results.status)}
                  Search Results
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(results.status)}`}>
                  {results.status === 'clear' ? 'No Conflicts' :
                   results.status === 'conflict' ? 'Conflict Found' :
                   results.status === 'potential' ? 'Potential Conflict' : results.status}
                </span>
              </div>

              {results.matches && results.matches.length > 0 ? (
                <div className="space-y-3">
                  {results.matches.map((match, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        match.match_type === 'adverse_party'
                          ? 'bg-red-900/20 border-red-500/30'
                          : 'bg-amber-900/20 border-amber-500/30'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          match.match_type === 'adverse_party'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {getMatchTypeIcon(match.match_type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{match.name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              match.match_type === 'adverse_party'
                                ? 'bg-red-500/30 text-red-300'
                                : 'bg-amber-500/30 text-amber-300'
                            }`}>
                              {match.match_type === 'adverse_party' ? 'Adverse Party' :
                               match.match_type === 'contact' ? 'Contact' :
                               match.match_type === 'case' ? 'Case' :
                               match.match_type === 'case_contact' ? 'Case Contact' : match.match_type}
                            </span>
                            <span className="text-virgo-teal-500 text-sm">
                              {match.match_score}% match
                            </span>
                          </div>
                          {match.case_name && (
                            <p className="text-sm text-virgo-teal-300 mt-1">
                              Case: {match.case_name}
                            </p>
                          )}
                          {match.role && (
                            <p className="text-sm text-virgo-teal-400 mt-0.5">
                              Role: {match.role}
                            </p>
                          )}
                          {match.reason && (
                            <p className="text-sm text-virgo-teal-400 mt-0.5">
                              {match.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-green-300 font-medium">No conflicts found</p>
                  <p className="text-virgo-teal-400 text-sm mt-1">
                    This search returned no matches in your database
                  </p>
                </div>
              )}

              {/* Clear button if there are conflicts */}
              {results.status !== 'clear' && results.check_id && (
                <div className="mt-4 pt-4 border-t border-virgo-teal-700/30">
                  <button
                    onClick={() => openClearModal(results)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Clear After Review
                  </button>
                  <p className="text-xs text-virgo-teal-500 mt-2">
                    Mark as cleared after verifying there is no actual conflict
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* History Panel */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-virgo-teal-400" />
            Recent Checks
          </h2>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-virgo-teal-400 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-virgo-teal-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No conflict checks yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {history.map((check) => (
                <div
                  key={check.id}
                  className="p-3 bg-virgo-navy-600/50 rounded-lg border border-virgo-teal-700/30"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(check.status)}
                      <span className="text-white font-medium text-sm">
                        {check.searched_name || check.searched_company || 'Unknown'}
                      </span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs border ${getStatusColor(check.status)}`}>
                      {check.status}
                    </span>
                  </div>
                  <div className="text-xs text-virgo-teal-400 space-y-0.5">
                    {check.searched_name && <p>Name: {check.searched_name}</p>}
                    {check.searched_company && <p>Company: {check.searched_company}</p>}
                    <p>
                      {new Date(check.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    {check.cleared_at && (
                      <p className="text-blue-400">
                        Cleared: {new Date(check.cleared_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Clear Conflict Modal */}
      {showClearModal && clearingCheck && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-blue-500/30 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-virgo-navy-600 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-t-xl">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Clear Conflict Check
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-virgo-teal-300">
                By clearing this conflict check, you're confirming that you've reviewed the potential matches and determined there is no actual conflict of interest.
              </p>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                  Notes (recommended)
                </label>
                <textarea
                  value={clearNotes}
                  onChange={(e) => setClearNotes(e.target.value)}
                  rows={3}
                  placeholder="Document your reasoning for clearing this conflict..."
                  className="w-full px-4 py-2.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearModal(false)}
                  className="flex-1 px-4 py-2.5 text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearConflict}
                  disabled={submittingClear}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {submittingClear ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4" />
                  )}
                  Confirm Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conflicts;
